import { readChartDocument } from "../store/personalData";

export const CHART_PAGE_SIZE = 20;
const uuid = value => typeof value === "string" && /^[\da-f]{8}(-[\da-f]{4}){3}-[\da-f]{12}$/i.test(value);
const failure = (message, code = "INVALID_RESPONSE") => Object.assign(new Error(message), { code });

export function chartErrorMessage(error) {
  if (error?.code === "P0001" && error?.message === "CHART_LIMIT_REACHED") return "You have reached the limit of 50 saved chart sets. Delete an unneeded chart before saving another. Your draft has been kept.";
  if (["42703", "PGRST202", "PGRST204"].includes(error?.code)) return "Database setup needs the chart Library migration. Your saved charts have not changed.";
  if (["PT409", "40001"].includes(error?.code)) return "This saved chart changed or is no longer available. Your draft has been kept. Refresh My Charts before reopening the saved version.";
  if (["42501", "PGRST301", "PGRST303"].includes(error?.code) || error?.status === 401) return "Your session could not access this chart. Please sign in again.";
  if (error?.code === "23514") return "This chart exceeds the storage limits or contains unsupported data. Your draft has been kept.";
  if (error?.code === "INVALID_DOCUMENT") return "This saved chart could not be read. Your current draft has been kept.";
  return "The request could not be confirmed. Your draft has been kept. Check My Charts before retrying a save.";
}

function row(value) {
  const data = Array.isArray(value) && value.length === 1 ? value[0] : value;
  if (!data || !uuid(data.id) || !Number.isSafeInteger(data.revision) || data.revision < 1) throw failure("Invalid chart response");
  const document = readChartDocument(data.document);
  if (!document) throw failure("Invalid saved chart", "INVALID_DOCUMENT");
  return { id: data.id, revision: data.revision, document };
}

// Pin the request's token. A later account switch must not send A's draft as B.
function request(query, context, write = false) {
  if (!context.accessToken) throw failure("Sign in required", "42501");
  query = query.setHeader("Authorization", `Bearer ${context.accessToken}`);
  if (write) query = query.retry(false);
  return context.signal ? query.abortSignal(context.signal) : query;
}

export async function listCharts(context, page = 0) {
  if (!Number.isSafeInteger(page) || page < 0) throw failure("Invalid page");
  const { data, error, count } = await request(context.client.from("chart_sets")
    .select("id,title,revision,updated_at,library_path", { count: "exact" })
    .order("updated_at", { ascending: false }).order("id", { ascending: true })
    .range(page * CHART_PAGE_SIZE, (page + 1) * CHART_PAGE_SIZE - 1), context);
  if (error) throw error;
  if (!Array.isArray(data) || !Number.isSafeInteger(count) || count < 0 || !data.every(item =>
    uuid(item.id) && typeof item.title === "string" && Number.isSafeInteger(item.revision) && item.revision > 0)) throw failure("Invalid chart list");
  return { items: data, total: count };
}

export async function getChart(context, id) {
  if (!uuid(id)) throw failure("Invalid chart ID");
  const { data, error } = await request(context.client.from("chart_sets")
    .select("id,revision,document").eq("id", id).maybeSingle(), context);
  if (error) throw error;
  if (!data) throw failure("Chart unavailable", "PT409");
  return row(data);
}

export async function createChart(context, document) {
  const valid = readChartDocument(document);
  if (!valid) throw failure("Invalid chart", "INVALID_DOCUMENT");
  const { data, error } = await request(context.client.rpc("create_chart", { p_document: valid }), context, true);
  if (error) throw error;
  return row(data);
}

export async function updateChart(context, id, revision, document) {
  const valid = readChartDocument(document);
  if (!uuid(id) || !Number.isSafeInteger(revision) || revision < 1 || !valid) throw failure("Invalid chart", "INVALID_DOCUMENT");
  const { data, error } = await request(context.client.rpc("update_chart", {
    p_id: id, p_revision: revision, p_document: valid,
  }), context, true);
  if (error) throw error;
  return row(data);
}

export async function deleteChart(context, id, revision) {
  if (!uuid(id) || !Number.isSafeInteger(revision) || revision < 1) throw failure("Invalid chart ID");
  const { data, error } = await request(context.client.rpc("delete_chart", { p_id: id, p_revision: revision }), context, true);
  if (error) throw error;
  if (data !== true) throw failure("Deletion could not be confirmed");
  return true;
}

export async function moveChart(context, id, revision, path) {
  if (!uuid(id) || !Number.isSafeInteger(revision) || revision < 1 || typeof path !== "string" || path.length > 1024 || Array.from(path).some(char => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127)) throw failure("Invalid chart location");
  const { data, error } = await request(context.client.rpc("move_chart", {
    p_id: id, p_revision: revision, p_library_path: path,
  }), context, true);
  if (error) throw error;
  return row(data);
}

export async function listLibraryCharts(context) {
  const items = new Map();
  for (let page = 0; ; page++) {
    if (context.signal?.aborted) throw new Error("Cancelled");
    const result = await listCharts(context, page);
    for (const item of result.items) items.set(item.id, item);
    if ((page + 1) * CHART_PAGE_SIZE >= result.total || result.items.length === 0) return [...items.values()];
  }
}
